import { useEffect, useRef, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Upload, Trash2, ImagePlus, Loader2 } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";

export default function ProductPhotosDialog({ product, open, onOpenChange, onChanged }) {
  const [images, setImages] = useState([]);
  const [uploading, setUploading] = useState(false);
  const inputRef = useRef(null);

  useEffect(() => {
    if (product && open) setImages(product.images || []);
  }, [product, open]);

  const handleUpload = async (fileList) => {
    if (!product?.id || !fileList?.length) return;
    setUploading(true);
    let added = 0;
    try {
      for (const file of Array.from(fileList)) {
        const form = new FormData();
        form.append("file", file);
        try {
          const r = await api.post(`/admin/products/${product.id}/photos`, form, {
            headers: { "Content-Type": "multipart/form-data" },
          });
          setImages((cur) => [r.data.url, ...cur]);
          added++;
        } catch (e) {
          toast.error(`${file.name}: ${e.response?.data?.detail || "errore upload"}`);
        }
      }
      if (added > 0) {
        toast.success(`${added} foto caricate`);
        onChanged?.();
      }
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  const handleDelete = async (url) => {
    if (!window.confirm("Rimuovere questa foto dal prodotto?")) return;
    try {
      await api.delete(`/admin/products/${product.id}/photos`, { params: { url } });
      setImages((cur) => cur.filter((i) => i !== url));
      toast.success("Foto rimossa");
      onChanged?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore rimozione");
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle className="font-admin flex items-center gap-2"><ImagePlus className="w-5 h-5 text-xd-cyanHover" /> Foto prodotto</DialogTitle>
          <DialogDescription className="text-xs">
            {product?.name} · {images.length} foto
          </DialogDescription>
        </DialogHeader>

        <div>
          <label
            htmlFor="photo-upload-input"
            data-testid="photo-upload-dropzone"
            onDragOver={(e) => { e.preventDefault(); }}
            onDrop={(e) => { e.preventDefault(); handleUpload(e.dataTransfer.files); }}
            className="flex flex-col items-center justify-center gap-2 py-8 border-2 border-dashed border-xd-border rounded-lg cursor-pointer hover:border-xd-cyan hover:bg-xd-cyan/5 transition-colors"
          >
            {uploading ? (
              <>
                <Loader2 className="w-6 h-6 text-xd-cyan animate-spin" />
                <span className="text-sm text-xd-muted">Caricamento…</span>
              </>
            ) : (
              <>
                <Upload className="w-6 h-6 text-xd-cyanHover" />
                <span className="text-sm text-xd-text font-medium">Clicca o trascina immagini qui</span>
                <span className="text-xs text-xd-muted">JPG, PNG, WEBP, GIF · max 8MB per file</span>
              </>
            )}
            <input
              id="photo-upload-input"
              ref={inputRef}
              type="file"
              multiple
              accept="image/*"
              onChange={(e) => handleUpload(e.target.files)}
              data-testid="photo-upload-input"
              className="hidden"
            />
          </label>

          <div className="mt-4 grid grid-cols-3 sm:grid-cols-4 gap-3 max-h-80 overflow-y-auto">
            {images.map((url, i) => (
              <div
                key={url + i}
                data-testid={`product-photo-${i}`}
                className="relative group aspect-square bg-slate-100 rounded-lg overflow-hidden border border-xd-border"
              >
                <img src={url} alt="" className="w-full h-full object-cover" />
                {i === 0 && (
                  <span className="absolute top-1 left-1 bg-xd-cyan text-white text-[9px] font-mono-tech uppercase tracking-widest px-1.5 py-0.5 rounded">Principale</span>
                )}
                <button
                  type="button"
                  onClick={() => handleDelete(url)}
                  data-testid={`product-photo-delete-${i}`}
                  className="absolute top-1 right-1 w-7 h-7 rounded-md bg-white/90 text-red-500 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center hover:bg-red-500 hover:text-white"
                  title="Rimuovi"
                >
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              </div>
            ))}
            {images.length === 0 && (
              <div className="col-span-full text-center text-sm text-xd-muted py-6">
                Nessuna foto caricata.
              </div>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
